// React
import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

// Libraries
import isNil from 'lodash/isNil'

// Components
import BInput from 'ui/blocks/Input'
import Icon from 'ui/components/Icon'

// Shared

/**
 * Input component with optional prepended icon
 *
 * @component
 * @example
 * <Input
 *   icon="user"
 *   field={field}
 *   size="md"
 *   type="text"
 * />
 *
 * @param {Object} [props] - Component props
 * @param {Object} [props.field] - Form field object containing value and onChange handler
 * @param {*} [props.field.value] - Current input value
 * @param {string} [props.type='text'] - HTML input type attribute
 * @param {('sm'|'md'|'lg')} [props.size='md'] - Size variant of the input
 * @param {boolean} [props.loading] - When true, replaces the icon with a spinning loader
 * @param {(string|boolean)} [props.icon] - Icon name to prepend to input, or false to hide
 * @param {Function} [props.inputRef] - Callback ref function to access the input DOM element
 * @param {boolean} [props.autoFocus] - Whether the input should automatically receive focus
 * @param {boolean} [props.preventFocusScroll] - When true with autoFocus, prevents browser from scrolling to the input when focused
 *
 * @returns {React.ReactElement} Input component with optional icon
 */
export const Input = ({
  field,
  type = 'text',
  size = 'md',
  loading,
  icon,
  inputRef,
  autoFocus,
  preventFocusScroll,
  ...props
}) => {
  const innerRef = useRef()

  useEffect(() => {
    if (!innerRef.current) return
    if (!autoFocus || !preventFocusScroll) return

    // Store the current scroll position
    const scrollPosition = window.scrollY

    // Focus the input
    innerRef.current.focus()

    // Restore the scroll position after the browser's auto-scroll
    setTimeout(() => {
      window.scrollTo(0, scrollPosition)
    }, 0)
  }, [autoFocus, preventFocusScroll])

  const value = isNil(field.value) ? '' : field.value
  const inputIcon = loading ? 'spinner-third' : icon

  return (
    <BInput size={size}>
      {icon && <Icon icon={inputIcon} spin={loading} />}
      <BInput.Input
        ref={(ref) => {
          innerRef.current = ref

          inputRef?.(ref)
        }}
        type={type}
        size={size}
        {...field}
        value={value}
        {...props}
        autoFocus={autoFocus && !preventFocusScroll}
      />
    </BInput>
  )
}

Input.propTypes = {
  field: PropTypes.object,
  type: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  autoFocus: PropTypes.bool,
  preventFocusScroll: PropTypes.bool,
  inputRef: PropTypes.func,
  loading: PropTypes.bool
}

export default Input
